import { useCallback, useEffect, useState } from "react";
import { apiDelete, apiGet, apiPost } from "../api/client";
import type { RetiredNode, RetireStatus } from "../api/types";
import { formatDateOnly } from "../lib/format";
import { navigate } from "../lib/router";
import { SectionCard } from "./SectionCard";

const RETIRE_TIP = "Hidden everywhere and not re-created from rosters; comes back only on a verified advert";

function retirePath(protocol: string, nativeId: string): string {
  return `/api/nodes/${encodeURIComponent(protocol)}/${encodeURIComponent(nativeId)}/retire`;
}

// Admin-only "Retire node" button with an inline optional-reason confirm.
// `onDone` runs after a successful retire; by default it returns to the node list.
export function RetireNodeButton({ protocol, nativeId, onDone }: { protocol: string; nativeId: string; onDone?: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = () => {
    setBusy(true);
    setError(null);
    apiPost<unknown>(retirePath(protocol, nativeId), { reason: reason.trim() === "" ? null : reason.trim() })
      .then(() => {
        setOpen(false);
        if (onDone) onDone();
        else navigate("#/nodes");
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Retire failed"))
      .finally(() => setBusy(false));
  };

  if (!open) {
    return (
      <button type="button" title={RETIRE_TIP} onClick={() => setOpen(true)}>
        Retire Node
      </button>
    );
  }
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }} title={RETIRE_TIP}>
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (optional)"
        aria-label="Retire reason"
        disabled={busy}
      />
      <button type="button" onClick={confirm} disabled={busy}>
        Confirm Retire
      </button>
      <button type="button" onClick={() => setOpen(false)} disabled={busy}>
        Cancel
      </button>
      {error && <span className="node-detail-note">{error}</span>}
    </div>
  );
}

// Shown on a node page whose node no longer exists. The URL carries only the
// native id, so both protocols are asked; whichever reports retired is shown.
export function RetiredNodeNotice({ nativeId }: { nativeId: string }) {
  const [statuses, setStatuses] = useState<RetireStatus[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    Promise.all(
      ["meshtastic", "meshcore"].map((p) => apiGet<RetireStatus>(retirePath(p, nativeId)).catch(() => null)),
    ).then((rs) => {
      if (cancelled) return;
      setStatuses(rs.filter((r): r is RetireStatus => r !== null && r.retired));
    });
    return () => {
      cancelled = true;
    };
  }, [nativeId]);

  useEffect(() => load(), [load]);

  const unretire = (protocol: string) => {
    setBusy(true);
    setError(null);
    apiDelete(retirePath(protocol, nativeId))
      .then(() => {
        load();
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Un-retire failed"))
      .finally(() => setBusy(false));
  };

  if (statuses.length === 0 && !error) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
      {statuses.map((s) => (
        <div key={s.protocol} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }} title={RETIRE_TIP}>
          <span className="node-detail-note">
            Retired {formatDateOnly(s.retired_at) ?? "(never advertised)"}
            {s.auto ? `; retires ${s.auto_retire_hours ?? 168} h after its last advert and returns on the next one` : ""}
          </span>
          {!s.auto && (
            <button type="button" onClick={() => unretire(s.protocol)} disabled={busy}>
              Un-retire
            </button>
          )}
        </div>
      ))}
      {error && <span className="node-detail-note">{error}</span>}
    </div>
  );
}

export function RetiredNodesList() {
  const [rows, setRows] = useState<RetiredNode[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    apiGet<RetiredNode[]>("/api/retired-nodes")
      .then((r) => setRows(r))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Load failed"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const unretire = (r: RetiredNode) => {
    apiDelete(retirePath(r.protocol, r.native_id))
      .then(() => load())
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Un-retire failed"));
  };

  return (
    <div title={RETIRE_TIP}>
      <SectionCard title="Retired Nodes">
        {error && <div className="node-detail-note">{error}</div>}
        {rows !== null && rows.length === 0 && <div>None</div>}
        {rows !== null && rows.length > 0 && (
          <div className="retired-table-wrap">
            <table className="retired-table">
              <thead>
                <tr>
                  <th>Protocol</th>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Retired</th>
                  <th>By</th>
                  <th>Last Advert</th>
                  <th>Reason</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const href = `#/nodes/${encodeURIComponent(r.native_id)}`;
                  return (
                    <tr key={`${r.protocol}:${r.native_id}:${r.auto ? "a" : "m"}`}>
                      <td data-label="Protocol">{r.protocol}</td>
                      <td data-label="ID">
                        <a href={href} title={r.native_id} className="retired-id">
                          {r.native_id.slice(0, 8)}
                        </a>
                      </td>
                      <td data-label="Name" className="retired-name">
                        {r.name ? <a href={href}>{r.name}</a> : ""}
                      </td>
                      <td data-label="Retired">{formatDateOnly(r.retired_at) ?? ""}</td>
                      <td data-label="By">{r.retired_by ? <span className="badge-outline">{r.retired_by}</span> : ""}</td>
                      <td data-label="Last Advert">{r.auto ? (formatDateOnly(r.last_advert_at ?? null) ?? "Never") : ""}</td>
                      <td data-label="Reason" className="retired-reason">{r.reason ?? ""}</td>
                      <td className="retired-action">
                        {!r.auto && (
                          <button type="button" onClick={() => unretire(r)}>
                            Un-retire
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
